import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import withProgress from 'core/components/progress/withProgress'
import Dropdown from 'core/elements/dropdown'
import useListAction from 'core/hooks/useListAction'
import { listNetworks } from 'openstack/components/networks/actions'
import {
  networksByIdSelector,
  subnetsFromAllNetworksSelector,
} from 'openstack/components/networks/selectors'

const AsyncDropdown = withProgress(Dropdown, { inline: true })

// Builds one dropdown option for a subnet (a subnet is an IP address range within a
// network). It also decides whether the option should be greyed-out/unselectable.
// FR-018: subnets whose ids are in disabledSubnetIds are ones already picked for this
// network, so they're shown disabled to stop the user choosing the same subnet twice.
function buildSubnetOption(subnet, network, includeNetworkName, disabledSubnetIds: string[] = []) {
  const networkPrefix = includeNetworkName ? `${network?.name || network?.id}: ` : ''
  const subnetLabel = `${networkPrefix}${subnet?.cidr} (${subnet?.name || subnet?.id})`
  const portAndIpsAvailable = network?.availablePortAndIPs > 0
  const alreadyUsed = disabledSubnetIds.includes(subnet?.id)

  if (alreadyUsed) {
    return {
      label: subnetLabel,
      value: subnet,
      disabled: true,
      tooltip: 'Subnet already selected for this network',
    }
  }

  return {
    label: subnetLabel,
    value: subnet,
    disabled: !portAndIpsAvailable,
    tooltip: !portAndIpsAvailable ? 'Network has no available IP addresses or ports' : null,
  }
}

// Dropdown for choosing a subnet. If a networkId is given it lists only that network's
// subnets, otherwise it lists subnets from all networks. Can optionally add "None" or
// "Automatic" choices, and disables subnets that are already in use.
export default function SubnetPicklist({
  disabled = false,
  disabledSubnetIds = [] as string[],
  name = 'subnet',
  label = 'Subnet',
  networkId = null,
  includeNoneOption = false,
  includeNetworkName = true,
  includeAutomaticOption = false,
  ...rest
}) {
  const { loading } = useListAction(listNetworks)
  const networksById = useSelector(networksByIdSelector)
  const subnets = useSelector(subnetsFromAllNetworksSelector)

  const networkSubnets = useMemo(() => {
    if (!networkId) {
      return subnets
    }
    return networksById[networkId]?.subnetDetails || []
  }, [networkId, subnets])

  const options = useMemo(() => {
    // will need to filter out taken private IPs
    const subnetOptions = networkSubnets.map((subnet) => {
      const network = networksById[subnet.network_id]
      return buildSubnetOption(subnet, network, includeNetworkName, disabledSubnetIds)
    })

    if (subnetOptions?.length === 0 && !includeNoneOption && !includeAutomaticOption) {
      return [{ label: 'No subnets available', value: null }]
    }

    if (includeNoneOption) {
      return [{ label: 'None', value: null }, ...subnetOptions]
    }

    if (includeAutomaticOption) {
      return [{ label: 'Automatic', value: '' }, ...subnetOptions]
    }

    return subnetOptions
  }, [
    networksById,
    networkSubnets,
    includeNoneOption,
    includeAutomaticOption,
    includeNetworkName,
    disabledSubnetIds,
  ])

  return (
    <AsyncDropdown
      {...rest}
      name={name}
      label={label}
      compact={false}
      disabled={disabled}
      loading={loading}
      items={options}
    />
  )
}
